/**
 * @fileoverview Navigation guards for authentication and authorization
 * @module router/guards
 *
 * Uses centralized branding for app name fallback
 * @see src/config/brandingDefaults.ts
 * @see src/config/subdomain.ts for subdomain detection
 * @see src/config/permissions.ts for role hierarchy
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantStore } from '@/stores/tenant'
import { IDENTITY_DEFAULTS } from '@/config/brandingDefaults'
import { isRoleAtLeast } from '@/config/permissions'
import { detectSubdomain, isReservedSubdomain } from '@/config/subdomain'
import { checkWelcomeCompleted } from '@/utils/welcomeStatus'

/**
 * Get allowed developer emails from environment
 */
function getDeveloperEmails(): string[] {
  const emails = import.meta.env.VITE_DEVELOPER_EMAILS || ''
  return emails.split(',').map((e: string) => e.trim().toLowerCase()).filter(Boolean)
}

/**
 * Check if an email is in the developer allowlist
 */
function isDeveloperEmail(email: string | null): boolean {
  if (!email) return false
  const allowedEmails = getDeveloperEmails()
  return allowedEmails.includes(email.toLowerCase())
}

/**
 * Wait for auth to initialize
 */
async function waitForAuth(): Promise<void> {
  const authStore = useAuthStore()

  if (authStore.initialized) return

  await authStore.init()
}

/**
 * Subdomain validation guard.
 * Validates URL subdomain exists in tenant registry.
 * Must run BEFORE authGuard in the guard chain.
 *
 * @see docs/standards.md - Multi-Tenancy Architecture
 */
export async function subdomainGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const tenantStore = useTenantStore()

  // Detect subdomain from URL (handles both staging and production patterns)
  const detectedSubdomain = detectSubdomain()

  // No subdomain or reserved subdomain - proceed normally
  if (!detectedSubdomain) {
    return next()
  }

  // Skip validation for error pages to prevent loops
  if (
    to.name === 'invalid-subdomain' ||
    to.name === 'unauthorized-tenant' ||
    to.name === 'not-found'
  ) {
    return next()
  }

  // Validate subdomain exists in tenant registry
  const tenantId = await tenantStore.loadBySubdomain(detectedSubdomain)

  if (!tenantId) {
    // Invalid subdomain - show error page
    return next({
      name: 'invalid-subdomain',
      query: { subdomain: detectedSubdomain }
    })
  }

  // Subdomain valid - proceed to auth checks
  next()
}

/**
 * Global auth guard - checks authentication and authorization
 */
export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  // PUBLIC ROUTES: Don't wait for auth - proceed immediately
  // Only wait for auth on routes that require it or are auth pages
  const needsAuthCheck = to.meta.requiresAuth ||
                         to.meta.requiresSuperAdmin ||
                         to.meta.requiredRole ||
                         to.meta.requiredPermission ||
                         to.meta.requiredFeature ||
                         to.path.startsWith('/auth')

  if (needsAuthCheck) {
    await waitForAuth()
  }

  const authStore = useAuthStore()

  // Check authentication requirement
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
  }

  // Redirect authenticated users on tenant subdomains from root to dashboard
  // This prevents showing the marketing page to logged-in users on their workspace
  const detectedSubdomain = detectSubdomain()
  if (
    detectedSubdomain &&
    !isReservedSubdomain(detectedSubdomain) &&
    authStore.initialized &&
    authStore.isAuthenticated &&
    to.path === '/'
  ) {
    return next({ name: 'dashboard' })
  }

  // Redirect authenticated users away from auth pages (only if auth is initialized)
  // Exception: Allow access to onboarding for users without a tenant
  if (to.path.startsWith('/auth') && authStore.initialized && authStore.isAuthenticated) {
    // Allow access to onboarding if user has no tenant
    if (to.name === 'onboarding' && !authStore.currentTenantId) {
      return next()
    }
    // Users with a tenant should go to dashboard
    if (authStore.currentTenantId) {
      return next({ name: 'dashboard' })
    }
    // Users without a tenant trying to access other auth pages should go to onboarding
    if (!authStore.currentTenantId && to.name !== 'onboarding') {
      return next({ name: 'onboarding' })
    }
  }

  // Check super admin requirement
  if (to.meta.requiresSuperAdmin && !authStore.isSuperAdmin) {
    // Return 404 instead of 403 for security (don't reveal admin exists)
    return next({ name: 'not-found' })
  }

  // Check developer requirement
  if (to.meta.requiresDeveloper && !isDeveloperEmail(authStore.userEmail)) {
    return next({ name: 'forbidden' })
  }

  // Check role requirement using centralized role hierarchy
  if (to.meta.requiredRole) {
    const userRole = authStore.currentRole
    const requiredRole = to.meta.requiredRole

    // Use centralized role comparison utility
    if (!userRole || !isRoleAtLeast(userRole, requiredRole)) {
      return next({ name: 'forbidden' })
    }
  }

  // Check permission requirement
  if (to.meta.requiredPermission) {
    if (!authStore.hasPermission(to.meta.requiredPermission)) {
      return next({ name: 'forbidden' })
    }
  }

  // Check enterprise feature requirement
  if (to.meta.requiredFeature) {
    const tenantStore = useTenantStore()
    if (!tenantStore.hasFeature(to.meta.requiredFeature)) {
      return next({ name: 'forbidden' })
    }
  }

  next()
}

/**
 * Tenant context guard - ensures tenant is loaded
 */
export async function tenantGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  if (!to.meta.requiresTenant) {
    return next()
  }

  const authStore = useAuthStore()
  const tenantStore = useTenantStore()

  // Must be authenticated for tenant routes
  if (!authStore.isAuthenticated) {
    return next()
  }

  // Check if user has tenant context
  if (!authStore.currentTenantId) {
    // User is authenticated but has no tenant - send to onboarding
    return next({ name: 'onboarding' })
  }

  // Load tenant data if not already loaded
  if (!tenantStore.profile) {
    await tenantStore.init()
  }

  // Check tenant status
  if (tenantStore.isSuspended) {
    return next({ name: 'suspended' })
  }

  next()
}

/**
 * Welcome completion guard - redirects to welcome wizard if not completed
 *
 * Checks both localStorage (fast path) and Firestore (cross-device sync)
 * to determine if user has completed the welcome wizard.
 *
 * @see utils/welcomeStatus.ts for implementation details
 */
export async function welcomeGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  // Skip check for welcome route itself
  if (to.path.startsWith('/app/welcome')) {
    return next()
  }

  // Only apply to authenticated app routes
  if (!to.path.startsWith('/app')) {
    return next()
  }

  const authStore = useAuthStore()

  // Must be authenticated
  if (!authStore.isAuthenticated) {
    return next()
  }

  // Check if user has completed welcome wizard (localStorage + Firestore fallback)
  const userId = authStore.user?.uid ?? null
  const tenantId = authStore.currentTenantId
  const hasCompletedWelcome = await checkWelcomeCompleted(userId, tenantId)

  if (!hasCompletedWelcome && to.name !== 'welcome') {
    return next({ name: 'welcome' })
  }

  next()
}

/**
 * Title guard - updates document title after navigation
 */
export function titleGuard(to: RouteLocationNormalized): void {
  const tenantStore = useTenantStore()
  const appName = tenantStore.tenantName || IDENTITY_DEFAULTS.companyName

  if (to.meta.title) {
    document.title = `${to.meta.title} | ${appName}`
  } else {
    document.title = appName
  }
}
